// เลือกคำตอบแชทจากไฟล์ที่เตรียมไว้ (src/data/chatbot/*.json) แทนการให้โมเดลแต่งเอง
//  ผู้ใช้พิมพ์ -> normalize ไทย -> จับคู่ char n-gram (2-3) TF-IDF/cosine กับ patterns
//  ลำดับ: extra_intents ของ persona -> intent ทั่วไป ; ต่ำกว่าเกณฑ์ -> unknown_fallback ของ persona
import { decorate, personaById, personaByName } from "./persona";

// ---- normalize ไทย ------------------------------------------------------------------------------
// คำลงท้าย/คำเสริม ตัดเฉพาะ "ท้ายคำ" (ท้ายข้อความหรือก่อนช่องว่าง/เครื่องหมาย) ไม่ตัดกลางคำ เช่น คะแนน
const PARTICLES = ["ครับผม", "ครับ", "คับ", "ค่ะ", "คะ", "ค่า", "จ้า", "จ้ะ", "จ๊ะ", "จ๋า", "นะ", "น้า", "เน้อ", "เนอะ", "อ่ะ", "อะ", "อ่า", "งับ", "ง่ะ", "ฮะ", "จร้า", "ค้าบ", "คร้าบ"];
const PARTICLE_RE = new RegExp("(?:" + PARTICLES.join("|") + ")$", "u");
const KEEP = /[^฀-๿a-z0-9一-鿿]/g; // เก็บ ไทย/ละติน/เลข/จีน
const TONES = /[่-์็]/g; // ไม้เอก โท ตรี จัตวา การันต์ ไม้ไต่คู้ — สะกดผิดวรรณยุกต์ยังจับได้

/** ข้อความผู้ใช้ -> สตริงสำหรับจับคู่ (ตัวพิมพ์เล็ก ไม่มีช่องว่าง/เครื่องหมาย/วรรณยุกต์/คำลงท้าย/555/ๆ/อักษรซ้ำยาว) */
export function normalize(text) {
  let s = String(text == null ? "" : text).normalize("NFC").toLowerCase();
  const laughy = /5{3,}|ฮ่า|ฮา{2,}|ขำ|คิกๆ|อิอิ|ฮิฮิ|หัวเราะ/.test(s);
  s = s.replace(/5{3,}/g, " ").replace(/ๆ/g, " ").replace(/ฯ/g, " ");
  const segs = s.split(/[\s\n\r\t.,!?…;:'"“”‘’()[\]{}<>*~_\-/\\|]+/).filter(Boolean);
  const out = segs.map((seg) => {
    let g = seg.replace(/\p{Extended_Pictographic}|️|‍/gu, "");
    for (let i = 0; i < 3; i++) {
      const m = g.match(PARTICLE_RE);
      if (!m || g.length - m[0].length < 2) break;
      g = g.slice(0, g.length - m[0].length);
    }
    return g;
  });
  s = out.join("").replace(KEEP, "").replace(TONES, "");
  s = s.replace(/(.)\1{2,}/gu, "$1"); // มากกกก -> มาก
  // ข้อความที่เหลือแต่เสียงหัวเราะ (555 / ฮ่าๆ) ให้เป็นโทเค็นเดียวกัน เพื่อจับคู่ intent laugh ได้
  if (!s.replace(/ฮา|ฮะ|ขำ/g, "") && laughy) return "ฮาฮา";
  if (!s && laughy) return "ฮาฮา";
  return s;
}

// ---- n-gram TF-IDF ---------------------------------------------------------------------------------
export function ngrams(s) {
  const t = Array.from("^" + s + "$");
  const out = [];
  for (let n = 2; n <= 3; n++) for (let i = 0; i + n <= t.length; i++) out.push(t.slice(i, i + n).join(""));
  return out;
}
const tf = (grams) => {
  const m = new Map();
  grams.forEach((g) => m.set(g, (m.get(g) || 0) + 1));
  return m;
};

/** entries: [{id, text}] -> index ; แต่ละ pattern เป็นเอกสารหนึ่งชิ้น (idf คิดข้าม patterns ทั้งชุด) */
export function buildIndex(entries) {
  const docs = [];
  const df = new Map();
  entries.forEach((e) => {
    const norm = normalize(e.text);
    if (norm.length < 1) return;
    const counts = tf(ngrams(norm));
    counts.forEach((_, g) => df.set(g, (df.get(g) || 0) + 1));
    docs.push({ id: e.id, norm, counts });
  });
  const N = docs.length;
  const idf = (g) => Math.log((N + 1) / ((df.get(g) || 0) + 1)) + 1;
  docs.forEach((d) => {
    const w = new Map();
    let sq = 0;
    d.counts.forEach((c, g) => {
      const x = (1 + Math.log(c)) * idf(g);
      w.set(g, x);
      sq += x * x;
    });
    d.w = w;
    d.sq = sq;
    d.norm2 = Math.sqrt(sq);
    delete d.counts;
  });
  return { docs, idf, N };
}

/**
 * คะแนน 0..1 ต่อ id (ค่าสูงสุดของ patterns ในกลุ่ม) : รวม cosine กับ "สัดส่วนที่ pattern ถูกครอบคลุมในประโยค"
 * (ประโยคยาวที่มี pattern อยู่ข้างใน เช่น "วันนี้เหนื่อยมากเลย" ยังจับได้) ; pattern สั้นมากลดน้ำหนักส่วนครอบคลุม
 */
export function scoreIndex(index, query) {
  const q = normalize(query);
  const res = new Map();
  if (!q) return { q, scores: res };
  const qc = tf(ngrams(q));
  const qw = new Map();
  let qsq = 0;
  qc.forEach((c, g) => {
    const x = (1 + Math.log(c)) * index.idf(g);
    qw.set(g, x);
    qsq += x * x;
  });
  const qn = Math.sqrt(qsq) || 1;
  index.docs.forEach((d) => {
    let dot = 0, cov = 0;
    d.w.forEach((x, g) => {
      const y = qw.get(g);
      if (y) {
        dot += x * y;
        cov += x * x;
      }
    });
    if (!dot) return;
    const cos = dot / (d.norm2 * qn);
    const covRatio = cov / d.sq;
    const len = Array.from(d.norm).length;
    const lenW = Math.min(1, Math.max(0, (len - 1) / 5)); // pattern ยาว <=1 ตัว ไม่มีน้ำหนักครอบคลุม ; >=6 ตัวเต็ม
    const exact = q === d.norm ? 1 : 0;
    const s = Math.min(1, 0.55 * cos + 0.45 * covRatio * lenW + 0.15 * exact);
    const prev = res.get(d.id);
    if (!prev || s > prev.best) res.set(d.id, { best: s, hits: (prev ? prev.hits : 0) + (s > 0.35 ? 1 : 0), pattern: d.norm });
    else if (s > 0.35) prev.hits++;
  });
  const scores = new Map();
  res.forEach((v, id) => scores.set(id, Math.min(1, v.best + Math.min(0.06, 0.02 * Math.max(0, v.hits - 1)))));
  return { q, scores };
}

const top = (scores) => {
  let id = null, s = 0;
  scores.forEach((v, k) => {
    if (v > s) {
      s = v;
      id = k;
    }
  });
  return { id, score: s };
};

export const THRESHOLD = { extra: 0.58, general: 0.4 };

// ---- บอท -------------------------------------------------------------------------------------------
/** hash สั้นของข้อความคำตอบ ใช้จำว่าเพิ่งตอบอะไรไป (เสถียรแม้ลำดับในไฟล์เปลี่ยน) */
export const replyKey = (text) => {
  let h = 5381;
  const s = String(text);
  for (let i = 0; i < s.length; i++) h = ((h * 33) ^ s.charCodeAt(i)) >>> 0;
  return h.toString(36);
};

/** เลือกจาก pool โดยไม่ซ้ำ keys ที่เพิ่งใช้ ; ถ้าหมดตัวเลือกยอมซ้ำตัวที่เก่าที่สุด */
export function pickFresh(pool, recent = [], rng = Math.random) {
  if (!pool.length) return null;
  const fresh = pool.filter((t) => recent.indexOf(replyKey(t)) === -1);
  if (fresh.length) return fresh[Math.floor(rng() * fresh.length) % fresh.length];
  let best = pool[0], bestAge = -1;
  pool.forEach((t) => {
    const age = recent.indexOf(replyKey(t)); // 0 = ใหม่สุด -> ต้องการตัวที่ index มากสุด (เก่าสุด)
    if (age > bestAge) {
      best = t;
      bestAge = age;
    }
  });
  return best;
}

const fill = (t, userName, charName, giftName) => String(t || "").replace(/\{\{\s*user\s*\}\}/gi, userName || "เธอ").replace(/\{\{\s*char\s*\}\}/gi, charName || "เรา").replace(/\{\{\s*gift\s*\}\}/gi, giftName || "ของขวัญ");

/**
 * สร้างบอทจากข้อมูลสองไฟล์ (th = chatbot_th.json, personas = chatbot_personas.json)
 * reply(personaName, text, {recent, rng, userName, charName, giftName, intent, decorate, reject}) -> {intent, score, source, base, reply, key}
 *  recent = รายการ key ของคำตอบล่าสุด (ใหม่สุดก่อน) ; base = ต้นฉบับก่อนตกแต่ง (ไว้ให้ Qwen ปรับคำ) ; key = ของ base
 */
export function createBot(th, personasFile) {
  const general = th.intents.filter((i) => i.patterns && i.patterns.length);
  const generalIndex = buildIndex(general.flatMap((i) => i.patterns.map((p) => ({ id: i.intent, text: p }))));
  const generalReplies = {};
  th.intents.forEach((i) => {
    generalReplies[i.intent] = i.responses || [];
  });
  const personas = {};
  (personasFile.personas || []).forEach((p) => {
    const extras = p.extra_intents || [];
    personas[p.persona] = {
      data: p,
      extraIndex: buildIndex(extras.flatMap((e) => (e.patterns || []).map((t) => ({ id: e.intent, text: t })))),
      extraReplies: Object.fromEntries(extras.map((e) => [e.intent, e.responses || []])),
    };
  });
  const personaNames = Object.keys(personas);

  function classify(personaName, text) {
    const P = personas[personaName];
    if (P && P.extraIndex.docs.length) {
      const { scores } = scoreIndex(P.extraIndex, text);
      const t = top(scores);
      if (t.id && t.score >= THRESHOLD.extra) return { intent: t.id, score: t.score, source: "extra" };
    }
    const { q, scores } = scoreIndex(generalIndex, text);
    const t = top(scores);
    if (t.id && t.score >= THRESHOLD.general) return { intent: t.id, score: t.score, source: "general" };
    return { intent: "unknown_fallback", score: t.score || 0, source: "fallback", q };
  }

  function poolOf(personaName, intent, source) {
    const P = personas[personaName];
    if (source === "extra" && P) return P.extraReplies[intent] || [];
    const own = P && P.data.responses && P.data.responses[intent];
    return own && own.length ? own : generalReplies[intent] || generalReplies.unknown_fallback || [];
  }

  function reply(personaName, text, opts = {}) {
    const rng = opts.rng || Math.random;
    // opts.intent = ระบุหัวข้อตรง ๆ (เช่น gift_small ไม่มี patterns) ข้ามการจับคู่ข้อความ
    const c = opts.intent ? { intent: opts.intent, score: 1, source: "forced" } : classify(personaName, text);
    let pool = poolOf(personaName, c.intent, c.source);
    if (opts.reject) {
      const ok = pool.filter((t) => !opts.reject(fill(t, opts.userName, opts.charName, opts.giftName))); // เช่น คำต้องห้ามของแชทนั้น
      if (ok.length) pool = ok;
    }
    const base = pickFresh(pool, opts.recent || [], rng) || "";
    const filled = fill(base, opts.userName, opts.charName, opts.giftName);
    const per = personaByName(personaName);
    const out = opts.decorate === false ? filled : decorate(filled, per, c.intent, rng);
    return { intent: c.intent, score: c.score, source: c.source, base: filled, reply: out, key: replyKey(base), poolSize: pool.length };
  }

  return { reply, classify, personaNames, hasPersona: (n) => !!personas[n], generalIndex };
}

// ---- โหลดแบบ lazy (แยก chunk ไม่บวม bundle หลัก) -----------------------------------------------------
let botPromise = null;
export function loadBot() {
  if (!botPromise) {
    botPromise = Promise.all([import("../../data/chatbot/chatbot_th.json"), import("../../data/chatbot/chatbot_personas.json")])
      .then(([th, ps]) => createBot(th.default || th, ps.default || ps))
      .catch((e) => {
        botPromise = null;
        throw e;
      });
  }
  return botPromise;
}

/** personaId (เช่น "big_sister") -> ผลตอบ ; ไม่รู้จัก id ใช้ชื่อ "นักเรียน" (เรา/เธอ) */
export async function answer(personaId, text, opts = {}) {
  const bot = await loadBot();
  const p = personaById(personaId) || personaById("student");
  return bot.reply(p.name, text, opts);
}

/** ตอบตามหัวข้อที่ระบุ (ไม่จับคู่ข้อความ) เช่น answerIntent(personaId, "gift_big", {giftName, userName}) */
export async function answerIntent(personaId, intent, opts = {}) {
  const bot = await loadBot();
  const p = personaById(personaId) || personaById("student");
  return bot.reply(p.name, "", { ...opts, intent });
}
